/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from 'react';
import { noop } from 'lodash';
import { useDispatch } from 'react-redux';
import AddIcon from '@material-ui/icons/Add';
import ComponentSectionFC from '../ComponentSectionFC';
import thunks from '../../../../../store/components/thunks';
import { Tooltip, Accordion, Paragraph } from '@cambridgeassessment/cambridge-ui';
import { Component, ComponentSections, ComponentSectionDoc } from '../../../../../service/components/types';
import {
  StyledAccordion,
  StyledComponentButton,
  StyledReadOnlyComponent,
  StyledAddButtonContainer,
  StyledComponentHeaderText,
  StyledExpansionPanelDetails,
  StyledComponentParagraphText,
  StyledComponentHeaderContainer,
} from './styles';

interface Props {
  readOnly?: boolean;
  component: Component;
  componentId: string;
  removeEnabled: boolean;
  disabled?: boolean;
}

const ComponentDocuments = ({ readOnly, component, componentId, removeEnabled, disabled }: Props): JSX.Element => {
  const dispatch = useDispatch();

  const [localComponentSections, setLocalComponentSections] = useState<ComponentSectionDoc[]>([]);
  const deleteLocalComponentSection = (): void => setLocalComponentSections([]);
  const [initComponentSectionsLength, setInitComponentSectionsLength] = useState(
    component.componentSections?.length || 0,
  );
  const [openedComponentSections, setOpenedComponentSections] = useState<number[]>([]);
  const doesSectionsExist = component && component.componentSections && component.componentSections.length > 0;
  const sortedComponentSections = useMemo(
    () => component.componentSections.sort((a, b) => a.id - b.id),
    [component.componentSections],
  );

  useEffect(() => {
    if (
      initComponentSectionsLength !== component.componentSections?.length &&
      component.componentSections?.length > 0
    ) {
      setOpenedComponentSections([
        ...openedComponentSections,
        component.componentSections[component.componentSections.length - 1].id,
      ]);
      setInitComponentSectionsLength(component.componentSections?.length || 0);
    }
  }, [component.componentSections]);

  const renameComponentSection = (
    componentId: string,
    componentSection: ComponentSections | ComponentSectionDoc,
  ): void => {
    const updatedComponentSection = componentSection as ComponentSections;
    dispatch(thunks.updateComponentSection(componentId, updatedComponentSection, 'name', updatedComponentSection.id));
  };

  const deleteComponentSection = (componentId: string, id: number): void => {
    dispatch(thunks.deleteComponentSection(componentId, id));
    setInitComponentSectionsLength(initComponentSectionsLength - 1);
  };

  const componentSectionNameCheck = (name: string, componentSectionsid: number): boolean => {
    const { componentSections } = component;

    if (componentSectionsid === 0 && localComponentSections.length === 1) {
      return !!(
        componentSections &&
        componentSections.find((componentSection: ComponentSections) => componentSection.name === name)
      );
    }

    return !!(
      componentSections &&
      componentSections.some(
        (componentSection: ComponentSections, currentIndex: number) =>
          currentIndex !== componentSectionsid && componentSection.name === name,
      )
    );
  };

  const createNewComponentSection = (
    componentId: string,
    componentSection: ComponentSections | ComponentSectionDoc,
  ): void => {
    dispatch(thunks.createComponentSection(componentId, componentSection));
    setLocalComponentSections([]);
  };

  const createLocalComponentSection = (): void => {
    const arr = [...localComponentSections, { name: '', fileTypes: [], isMandatory: true, allowMultipleFiles: true }];
    setLocalComponentSections(arr);
  };

  const openComponentSection = (componentSection: ComponentSections): void => {
    if (openedComponentSections.includes(componentSection.id)) {
      setOpenedComponentSections(openedComponentSections.filter((x) => x !== componentSection.id));
    } else {
      setOpenedComponentSections([...openedComponentSections, componentSection.id]);
    }
  };

  return (
    <StyledExpansionPanelDetails data-qa-id='component-documents'>
      <StyledComponentHeaderContainer>
        <StyledComponentHeaderText data-qa-id='component-documents-header'>
          Component documents
        </StyledComponentHeaderText>
      </StyledComponentHeaderContainer>
      <Paragraph data-qa-id='component-documents-p1'>
        Additional documents that a centre may send alongside the candidate work, for example a Supervisors Report.
      </Paragraph>
      <Paragraph data-qa-id='component-documents-p2'>
        Component Level Documents are not accepted in RM for examined components.
      </Paragraph>
      {component && component.componentSections && component.componentSections.length === 0 && (
        <StyledComponentParagraphText data-qa-id='empty-component-documents-list'>
          There are no component documents created.
        </StyledComponentParagraphText>
      )}
      {doesSectionsExist &&
        sortedComponentSections.map((componentSection: ComponentSections, i: number) => {
          const componentSectionId = `${componentSection.name}-${i}`;
          return (
            <StyledAccordion key={componentSectionId} data-qa-id={`component-section-${componentSection.id}`}>
              <Accordion
                key={componentSectionId}
                headerContent={componentSection.name}
                dataQaId='component-section'
                expanded={openedComponentSections.includes(componentSection.id)}
                onClick={() => openComponentSection(componentSection)}
              >
                <ComponentSectionFC
                  key={componentSectionId}
                  componentSection={componentSection}
                  submit={renameComponentSection}
                  remove={deleteComponentSection}
                  componentId={componentId}
                  index={i}
                  removeEnabled={removeEnabled}
                  deleteLocalSection={noop}
                  checkName={componentSectionNameCheck}
                  disabled={disabled}
                />
              </Accordion>
            </StyledAccordion>
          );
        })}
      {localComponentSections.map((componentSection: ComponentSectionDoc, i: number) => (
        <ComponentSectionFC
          key={`${componentSection.name}-${i}`}
          componentSection={componentSection}
          submit={createNewComponentSection}
          remove={deleteLocalComponentSection}
          componentId={componentId}
          index={i}
          removeEnabled={removeEnabled}
          checkName={componentSectionNameCheck}
          deleteLocalSection={deleteLocalComponentSection}
          disabledOptions={true}
          isLocalComponentSection
        />
      ))}
      <StyledReadOnlyComponent data-qa-id={`read-only-button-wrapper-${readOnly}`} readOnly={readOnly}>
        <Tooltip
          arrow={true}
          placement='bottom-start'
          dataQaId='group-upload'
          title='Configure components to allow for submission at a component level'
          enterDelay={500}
        >
          <StyledAddButtonContainer>
            <StyledComponentButton
              onClick={createLocalComponentSection}
              color='primary'
              qaId={`expansion-details-add-component-button-${component.name}`}
              variant='outlined'
              type='submit'
              size='medium'
              disabled={localComponentSections.length > 0 || disabled}
              startIcon={<AddIcon />}
            >
              Add component document
            </StyledComponentButton>
          </StyledAddButtonContainer>
        </Tooltip>
      </StyledReadOnlyComponent>
    </StyledExpansionPanelDetails>
  );
};

export default ComponentDocuments;
